import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, dayLabel, hhmm, useAction, useResource } from '@limuzin/kit';
import type { ConfigView, SupportMessageView, SupportThreadView } from '@limuzin/passenger';
import {
  deleteSupportMessages, deleteSupportThread, getConfig, getSupportAttachment, getSupportThread,
  getSupportThreads, markSupportRead,
} from '../api';
import { askToNotify, threadTag } from '../notify';
import { NEW, abandon, discard, onDelivered, retry, send, usePending, type OutboxMessage } from '../outbox';
import { useSession } from '../session';
import { Icon } from '../material';
import { Button, ConfirmSheet, EmptyCard, Page, Spinner, useBack, useErrorHaptic } from '../widgets';
import {
  Bubble, ChatGallery, ChatHistory, ChatLayout, Composer, DeleteMessagesSheet, FailedSheet,
  OutboxBubble, Selectable, outboxPhotos, useSelection, type ChatPhoto,
} from './conversation';

/**
 * «Поддержка» — the inbox: every question this passenger has asked, and the answers
 * under them. A port of `support_screen.dart`.
 *
 * A list of conversations rather than a form, because an answer comes back into the
 * app instead of to an email address somebody typed (ADR 0011). The only thing a
 * passenger has to decide is whether this is a new question or another line under
 * one they already asked.
 */
export function SupportScreen() {
  const { api, supportSignal, refreshSupport } = useSession();
  const navigate = useNavigate();
  const threads = useResource<SupportThreadView[]>(() => getSupportThreads(api), [api, supportSignal]);
  const config = useResource<ConfigView>(() => getConfig(api), [api]);
  const [removing, setRemoving] = useState<SupportThreadView | null>(null);
  const support = config.data?.supportPhone ?? null;

  const remove = useAction(async () => {
    if (!removing) return;
    await deleteSupportThread(api, removing.id);
    // Nothing is still on its way to a conversation that no longer exists.
    abandon('support', removing.id);
    setRemoving(null);
    threads.reload();
    refreshSupport();
  });

  const rows = threads.data ?? [];

  return (
    <Page
      title="Поддержка"
      centred
      fallback="/profile"
      scroll={false}
      trailing={
        support && (
          <a
            className="icon-btn go"
            href={`tel:${support}`}
            aria-label="Позвонить в поддержку"
            title="Позвонить в поддержку"
          >
            <Icon name="call_outlined" size={20} />
          </a>
        )
      }
    >
      <div className="screen-scroll">
        <div className="column support-list">
          {threads.loading ? (
            <Spinner />
          ) : threads.error && !threads.data ? (
            <EmptyCard title="Не открылось" hint={threads.error.message} />
          ) : rows.length === 0 ? (
            <EmptyCard
              title="Здесь будут ваши вопросы"
              hint="Проблема с поездкой, что-то не работает в приложении или есть предложение — напишите, и ответ придёт сюда же."
            />
          ) : (
            rows.map((thread) => (
              <ThreadCard
                key={thread.id}
                thread={thread}
                onOpen={() => navigate(`/support/${thread.id}`)}
                onHold={() => {
                  remove.clearError();
                  setRemoving(thread);
                }}
              />
            ))
          )}
        </div>
      </div>
      {/* The new question stays under the thumb however long the list grows. */}
      <div className="column support-foot">
        <Button size="foot" icon="edit_outlined" onClick={() => navigate(`/support/${NEW}`)}>
          Новый вопрос
        </Button>
      </div>
      {removing && (
        <RemoveThreadSheet
          busy={remove.pending}
          error={remove.error}
          onConfirm={() => void remove.run()}
          onClose={() => setRemoving(null)}
        />
      )}
    </Page>
  );
}

/**
 * «Удалить переписку?» — asked in both places that offer it: a row of the inbox held
 * down, and the menu in the bar of the conversation. The thread goes on the server,
 * with its messages and its screenshots, and support loses it as well.
 */
function RemoveThreadSheet({
  busy,
  error,
  onConfirm,
  onClose,
}: {
  busy: boolean;
  error: ApiError | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmSheet
      title="Удалить переписку?"
      hint="Вопрос, ответы на него и приложенные фото исчезнут — и у вас, и у поддержки. Вернуть их будет нельзя."
      label="Удалить переписку"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/** «14:32» today, «Вчера», «7 сентября» before that — an inbox says when. */
function inboxWhen(at: string): string {
  const date = new Date(at);
  const label = dayLabel(date);
  return label === 'Сегодня' ? hhmm(date) : label;
}

/**
 * One conversation as a row of the inbox: its number, the last thing said in it and
 * when, and the red dot while an answer is unopened. Held down, it offers to go.
 */
function ThreadCard({
  thread,
  onOpen,
  onHold,
}: {
  thread: SupportThreadView;
  onOpen: () => void;
  onHold: () => void;
}) {
  const timer = useRef(0);
  const held = useRef(false);
  const unread = thread.unread > 0;
  return (
    <button
      type="button"
      className={unread ? 'thread-card unread' : 'thread-card'}
      onPointerDown={() => {
        held.current = false;
        timer.current = window.setTimeout(() => {
          held.current = true;
          onHold();
        }, 500);
      }}
      onPointerUp={() => window.clearTimeout(timer.current)}
      onPointerLeave={() => window.clearTimeout(timer.current)}
      onPointerCancel={() => window.clearTimeout(timer.current)}
      onContextMenu={(event) => {
        event.preventDefault();
        window.clearTimeout(timer.current);
        if (!held.current) onHold();
        held.current = true;
      }}
      onClick={() => {
        if (!held.current) onOpen();
        held.current = false;
      }}
    >
      <span className="row" style={{ alignItems: 'flex-start', gap: 0 }}>
        <span className="grow ellipsis thread-title">Обращение №&nbsp;{thread.number}</span>
        <span className="caption" style={{ fontSize: 12, marginLeft: 10 }}>
          {inboxWhen(thread.lastMessageAt)}
        </span>
        {unread && <span className="thread-dot pulse" role="img" aria-label="Есть непрочитанные ответы поддержки" />}
      </span>
      {/* Who spoke last, the way an inbox says it — «Поддержка:» is the line a
          passenger is looking for. */}
      <span className="thread-preview">
        {thread.lastFromSupport ? 'Поддержка' : 'Вы'}: {thread.preview}
      </span>
    </button>
  );
}

type Row = { id: string; sentAt: string; sent?: SupportMessageView; sending?: OutboxMessage };

/**
 * One «Обращение» — the question, the answer, and the box to say the next thing. A
 * port of `support_thread_screen.dart`.
 *
 * The same screen opens a conversation that exists and starts one that does not: a
 * new question is an empty thread with an invitation in it, and the first message
 * sent from here is what brings the thread into being — «Новый вопрос» then becomes
 * «Обращение № 42» without anybody asking again.
 */
export function SupportThreadScreen() {
  const { threadId = NEW } = useParams();
  return <Thread key={threadId} initial={threadId === NEW ? null : threadId} />;
}

function Thread({ initial }: { initial: string | null }) {
  const { api, supportSignal, reading, refreshSupport } = useSession();
  const navigate = useNavigate();
  const back = useBack('/support');
  const [id, setId] = useState(initial);
  const [thread, setThread] = useState<SupportThreadView | null>(null);
  const [loading, setLoading] = useState(initial !== null);
  const [error, setError] = useState<ApiError | null>(null);
  useErrorHaptic(error?.message);
  const [menu, setMenu] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [failed, setFailed] = useState<OutboxMessage | null>(null);
  const [asking, setAsking] = useState<{ ids: string[]; done: (gone: boolean) => void } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ApiError | null>(null);
  const pending = usePending('support', id ?? NEW);

  const load = useCallback(
    async (which: string) => {
      try {
        const got = await getSupportThread(api, which);
        setThread(got);
        setError(null);
        // Read here rather than in the inbox: a badge goes out when the answer has
        // been in front of somebody, not when the row scrolled past.
        await markSupportRead(api, which).catch(() => {});
        refreshSupport();
      } catch (problem) {
        if (problem instanceof ApiError) setError(problem);
      } finally {
        setLoading(false);
      }
    },
    [api, refreshSupport],
  );

  useEffect(() => {
    if (id) void load(id);
    // `supportSignal` is an answer arriving, or messages going on the other side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, supportSignal]);

  // The message that opened the thread brings its id back with it.
  useEffect(
    () =>
      onDelivered((delivery) => {
        if (delivery.kind !== 'support') return;
        if (delivery.was === NEW && id === null) {
          setId(delivery.conversation);
          navigate(`/support/${delivery.conversation}`, { replace: true });
        } else if (delivery.conversation === id) void load(delivery.conversation);
      }),
    [id, load, navigate],
  );

  // On screen: an answer to this thread is not notified while it is read. And
  // somebody writing to support would like to hear back — the moment a browser will
  // put the question of notifications to them.
  useEffect(() => {
    askToNotify();
    if (!id) return;
    reading(threadTag(id));
    return () => reading(null);
  }, [id, reading]);

  const messages = thread?.messages ?? [];
  const rows = useMemo<Row[]>(
    () => [
      ...messages.map((message) => ({ id: message.id, sentAt: message.sentAt, sent: message })),
      ...pending.map((message) => ({ id: message.id, sentAt: message.sentAt, sending: message })),
    ],
    [messages, pending],
  );
  const pendingIds = new Set(pending.map((message) => message.id));

  const selection = useSelection(
    rows.map((row) => row.id),
    (ids) =>
      new Promise((done) => {
        setDeleteError(null);
        setAsking({ ids, done });
      }),
  );

  const photosOf = (message: SupportMessageView): ChatPhoto[] =>
    message.attachments.map((attachment) => ({
      id: attachment,
      author: message.author === 'SUPPORT' ? 'Поддержка' : 'Вы',
      sentAt: message.sentAt,
      caption: message.text,
      load: () => getSupportAttachment(api, attachment),
    }));

  const gallery = rows.flatMap((row) => (row.sent ? photosOf(row.sent) : outboxPhotos(row.sending!)));

  const remove = useAction(async () => {
    if (!id) return;
    await deleteSupportThread(api, id);
    abandon('support', id);
    refreshSupport();
    setRemoving(false);
    back();
  });

  return (
    <ChatLayout
      // Called by its number from the moment the first message opens it: the first
      // line is something somebody said, not the name of the conversation.
      title={thread ? `Обращение № ${thread.number}` : 'Новый вопрос'}
      subtitle="Поддержка"
      support
      action={
        thread && (
          <span style={{ position: 'relative' }}>
            <button
              type="button"
              className="icon-btn"
              aria-label="Действия с перепиской"
              title="Действия с перепиской"
              aria-expanded={menu}
              onClick={() => setMenu(!menu)}
            >
              <Icon name="more_vert" size={24} />
            </button>
            {menu && (
              <>
                <span className="menu-scrim" onClick={() => setMenu(false)} />
                <span className="menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenu(false);
                      remove.clearError();
                      setRemoving(true);
                    }}
                  >
                    <Icon name="delete_outline" size={20} />
                    Удалить переписку
                  </button>
                </span>
              </>
            )}
          </span>
        )
      }
      selection={selection}
      onBack={back}
    >
      {loading ? (
        <Spinner />
      ) : (
        <ChatGallery photos={gallery} send={(file) => send('support', id ?? NEW, '', [file])}>
          <ChatHistory
            rows={rows}
            id={(row) => row.id}
            sentAt={(row) => row.sentAt}
            departs={(row) => row.sending === undefined}
            empty={
              <div className="chat-invitation">
                <Icon name="forum_outlined" size={38} style={{ color: 'var(--faint)' }} />
                <p className="body" style={{ margin: '14px 0 0' }}>
                  Проблема с поездкой, что-то не работает в приложении или есть предложение —
                  напишите. Ответим здесь же, обычно в течение 24 часов.
                </p>
              </div>
            }
            render={(row) => {
              const mine = row.sending !== undefined || row.sent!.author !== 'SUPPORT';
              return (
                <Selectable id={row.id} mine={mine} selection={selection}>
                  {row.sending ? (
                    <OutboxBubble message={row.sending} onFailedTap={() => setFailed(row.sending!)} />
                  ) : (
                    <Bubble
                      mine={mine}
                      support={!mine}
                      text={row.sent!.text}
                      sentAt={row.sent!.sentAt}
                      photos={photosOf(row.sent!)}
                      voices={(row.sent!.voiceAttachments ?? []).map((voice) => ({
                        id: voice,
                        load: () => getSupportAttachment(api, voice),
                      }))}
                    />
                  )}
                </Selectable>
              );
            }}
          />
        </ChatGallery>
      )}
      {error && <div className="chat-error">{error.message}</div>}
      <Composer maxPhotos={4} onSend={(text, files, voice) => send('support', id ?? NEW, text, files, voice)} />

      {removing && (
        <ConfirmSheet
          title="Удалить переписку?"
          hint="Вопрос, ответы на него и приложенные фото исчезнут — и у вас, и у поддержки. Вернуть их будет нельзя."
          label="Удалить переписку"
          busy={remove.pending}
          error={remove.error}
          onConfirm={() => void remove.run()}
          onClose={() => setRemoving(false)}
        />
      )}
      {failed && (
        <FailedSheet
          onRetry={() => {
            retry(failed.id);
            setFailed(null);
          }}
          onDiscard={() => {
            discard(failed.id);
            setFailed(null);
          }}
          onClose={() => setFailed(null)}
        />
      )}
      {asking && (
        <DeleteMessagesSheet
          count={asking.ids.length}
          otherPerson="у поддержки"
          anySent={asking.ids.some((one) => !pendingIds.has(one))}
          busy={deleting}
          error={deleteError}
          onClose={() => {
            asking.done(false);
            setAsking(null);
          }}
          onDelete={(forEveryone) => {
            asking.ids.filter((one) => pendingIds.has(one)).forEach(discard);
            const sent = asking.ids.filter((one) => !pendingIds.has(one));
            if (sent.length === 0 || !id) {
              asking.done(true);
              setAsking(null);
              return;
            }
            setDeleting(true);
            void deleteSupportMessages(api, id, sent, forEveryone)
              .then(async () => {
                setThread((current) =>
                  current ? { ...current, messages: current.messages.filter((m) => !sent.includes(m.id)) } : current,
                );
                asking.done(true);
                setAsking(null);
                await load(id);
              })
              .catch((problem: unknown) =>
                setDeleteError(
                  problem instanceof ApiError
                    ? problem
                    : new ApiError('unknown', 'Не удалось удалить сообщения. Попробуйте ещё раз.'),
                ),
              )
              .finally(() => setDeleting(false));
          }}
        />
      )}
    </ChatLayout>
  );
}

